import type { Program } from "./schemas/program.js";
import type { AggregatorExerciseRow } from "./volumeByMuscle.js";
import { musclesWithDirectHardSets } from "./directHardSetMuscles.js";
import { prescribedHardSetsByExercise, roundIsNotWeekOf } from "./programPrescription.js";
import { resolveFullCycleSessions } from "./programResolution.js";
import {
  computeMuscleAdjacency,
  type AdjacencyDay,
  type MuscleAdjacencyWarning,
} from "./muscleAdjacency.js";
import {
  HABITUAL_WEEKDAYS_UNKNOWN,
  type HabitualWeekdays,
  type HabitualWeekdaysSource,
} from "./habitualWeekdays.js";
import { pickAuthoredText } from "./authoredText.js";
import { programRoundLabel } from "./programRoundTypes.js";
import type { Locale } from "./locale.js";

// Program adapter for the muscle-adjacency check. Expands the program's
// FULL repeating macrocycle (every week of the cycle, concatenated) into
// an ordered day sequence, resolving each day's muscles, then runs the
// cyclic core so the last day wraps onto the first.
//
// Each day's exercises come from resolveProgramSession — the SAME
// active-variant picker the logger's "Start session" flow runs — so a
// week's alternating / phase-offset variants resolve to the exercises
// actually trained that week. Two variants that never co-occur don't
// produce a false adjacency.
//
// #508: only a direct hard set puts a muscle on a day, never a synergist.
// #522: and only at least N of them (directHardSetMuscles.ts, the same rule
// the logged-session check uses). With no usual weekdays the sessions are
// spread evenly over the week and the result says so (`assumedSchedule`).
// Distances are calendar days only when the usual weekdays are known and a
// round is a week; any other case is day order, and the result says why.
//
// A (session, week) cell with no active entries still occupies a position
// in the sequence: it's a scheduled day the athlete may rest, and keeping
// it preserves the true distance between the trained days around it.

export type AdjacencyMeasure = "calendar" | "ordinal" | "assumed-calendar";

export type OrdinalAdjacencyReason =
  | "round-is-not-week"
  | "weekdays-unknown"
  | "weekday-count-mismatch"
  | "read-failed";

export type ProgramAdjacency = {
  /** Per muscle, the adjacent days it gets a direct hard set on. */
  pairs: MuscleAdjacencyWarning[];
  /** The weekday (0 = Sunday) each program day lands on, in day order.
   *  null when measured by day order. */
  weekdaysUsed: number[] | null;
  source: HabitualWeekdaysSource;
  measure: AdjacencyMeasure;
  /** Why the measure is the day order. */
  ordinalReason?: OrdinalAdjacencyReason;
  /** #522: present when no usual weekdays were known and the sessions were
   *  spread evenly over the week (`weekdaysUsed` holds that spread). */
  assumedSchedule?: { kind: "even-spread"; weekdays: number[]; note: string };
};

// #522: sessions per week -> the weekdays (0 = Sunday) they are assumed on
// when the athlete has no usual days. The most even spread over seven days
// that keeps Monday first; 4 sessions give Mon/Tue/Thu/Fri.
const EVEN_SPREAD_WEEKDAYS: Record<number, number[]> = {
  1: [1],
  2: [1, 4],
  3: [1, 3, 5],
  4: [1, 2, 4, 5],
  5: [1, 2, 4, 5, 6],
  6: [1, 2, 3, 4, 5, 6],
  7: [1, 2, 3, 4, 5, 6, 0],
};

// English: the labels feed the LLM-facing message.
const WEEKDAY_EN = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const programToAdjacencyDays = (
  program: Program,
  exerciseBySlug: ReadonlyMap<string, AggregatorExerciseRow>,
  // Reader's language (PE-21): the program's authored text is resolved
  // reader → author → any available. Omitted → the chain starts at the
  // author, never at English.
  locale?: Locale,
  // #508: one weekday per session, in day order. Omitted: day order.
  habitualWeekdays?: ReadonlyArray<number>,
): AdjacencyDay[] => {
  const multiWeek = program.cycleWeeks > 1;
  const first = habitualWeekdays?.[0];
  return resolveFullCycleSessions(program, locale).map((resolved, ordinal) => {
    const muscles = musclesWithDirectHardSets(
      prescribedHardSetsByExercise(resolved),
      exerciseBySlug,
    );
    const dayName =
      pickAuthoredText(
        program.sessions[resolved.sessionIdx]?.name,
        locale,
        program.authorLocale,
      ) ?? `Day ${resolved.sessionIdx + 1}`;
    const label = multiWeek ? `${programRoundLabel(resolved.weekIdx + 1)} ${dayName}` : dayName;
    const weekday = habitualWeekdays?.[resolved.sessionIdx];
    const onCalendar = weekday !== undefined && first !== undefined;
    return {
      key: `w${resolved.weekIdx}s${resolved.sessionIdx}`,
      label: onCalendar ? `${label} (${WEEKDAY_EN[weekday]})` : label,
      position: onCalendar ? resolved.weekIdx * 7 + ((weekday - first + 7) % 7) : ordinal,
      muscles,
    };
  });
};

/** The one computation behind the composer's warnings and the simulator. */
export const programAdjacency = (
  program: Program,
  exerciseBySlug: ReadonlyMap<string, AggregatorExerciseRow>,
  habitual: HabitualWeekdays = HABITUAL_WEEKDAYS_UNKNOWN,
  locale?: Locale,
): ProgramAdjacency => {
  const known = habitual.weekdays;
  const spread = EVEN_SPREAD_WEEKDAYS[program.sessions.length];
  // #530: more usual days than sessions (5 days, a 4-session program) cannot be
  // matched to the sessions either, and day order would flag every muscle: the
  // even spread is the better guess, and the note says the usual days were not used.
  const tooManyUsual = known !== null && known.length > program.sessions.length;
  const assumed =
    (known === null || tooManyUsual) &&
    habitual.readFailed !== true &&
    spread !== undefined &&
    !roundIsNotWeekOf(program);
  const weekdays = assumed ? spread : known;
  if (weekdays === null || roundIsNotWeekOf(program) || weekdays.length !== program.sessions.length) {
    const reason: OrdinalAdjacencyReason = roundIsNotWeekOf(program)
      ? "round-is-not-week"
      : weekdays === null
        ? habitual.readFailed === true
          ? "read-failed"
          : "weekdays-unknown"
        : "weekday-count-mismatch";
    const days = programToAdjacencyDays(program, exerciseBySlug, locale, undefined);
    return {
      pairs: computeMuscleAdjacency(days, {
        cyclic: true,
        cycleLength: days.length,
        measure: { kind: "ordinal", reason },
      }),
      weekdaysUsed: null,
      source: habitual.source,
      measure: "ordinal",
      ordinalReason: reason,
    };
  }
  const days = programToAdjacencyDays(program, exerciseBySlug, locale, weekdays);
  const note = assumed
    ? `${
        tooManyUsual
          ? `The ${known?.length} usual training weekdays are more than the ${program.sessions.length} sessions per week, so they cannot be matched to the sessions`
          : "No usual training weekdays are known"
      }: the ${weekdays.length} sessions per week are assumed on ${weekdays.map((d) => WEEKDAY_EN[d]).join(", ")}.`
    : undefined;
  return {
    pairs: computeMuscleAdjacency(days, {
      cyclic: true,
      cycleLength: 7 * program.cycleWeeks,
      measure: note !== undefined ? { kind: "assumed", note } : { kind: "calendar" },
    }),
    weekdaysUsed: [...weekdays],
    source: habitual.source,
    measure: assumed ? "assumed-calendar" : "calendar",
    ...(note !== undefined
      ? { assumedSchedule: { kind: "even-spread" as const, weekdays: [...weekdays], note } }
      : {}),
  };
};

export const checkProgramMuscleAdjacency = (
  program: Program,
  exerciseBySlug: ReadonlyMap<string, AggregatorExerciseRow>,
  // Reader's language (PE-21): the program's authored text is resolved
  // reader → author → any available. Omitted → the chain starts at the
  // author, never at English.
  locale?: Locale,
  habitual?: HabitualWeekdays,
): MuscleAdjacencyWarning[] =>
  programAdjacency(program, exerciseBySlug, habitual, locale).pairs;
